import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Checkbox} from "@material-tailwind/react";
import DataTable from "../../../components/DataTable/DataTable";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useNavigate} from "react-router-dom";
import {CurrenciesResponse, Currency} from "../../../types/Responses/CurrenciesResponse";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../../reducers/sidebar";
import FilterChips, {FilterChipDefinition} from "../../../components/FilterChips";
import DropdownInput from "../../../components/DropdownInput";

type CurrencyDataTableRequest = DataTableRequest & { crypto?: 0 | 1 | 2 };

const Currencies = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const user = useUserContext();
    const navigate = useNavigate();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [currencies, setCurrencies] = useState<CurrenciesResponse>();
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const defaultRequest = useMemo<CurrencyDataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<CurrencyDataTableRequest>(defaultRequest);
    const crypto = (dataTableRequest.crypto ?? 0) as 0 | 1 | 2;

    const sendRequest = useCallback((request: DataTableRequest) => {
        setIsDataTableLoading(true);
        API.getCurrencies(request).then((response: AxiosResponse) => {
            setCurrencies(response.data as CurrenciesResponse);
            setIsDataTableLoading(false);
        }).catch((e) => ErrorHandler(e, `get${APILink.GET_CURRENCIES}`, toast, handleError, setIsForbidden));
    }, [toast, handleError, setIsDataTableLoading]);

    const onDataTableChanged = useCallback((request: CurrencyDataTableRequest) => {
        const updatedRequest = crypto > 0 ? {...request, crypto} : {...request};
        persistDataTableRequest(updatedRequest);
        if (!updatedRequest.search || updatedRequest.search.length > 3) {
            sendRequest(!updatedRequest.search ? {...updatedRequest, search: ''} : updatedRequest);
        }
    }, [crypto, persistDataTableRequest, sendRequest]);

    const deleteCurrency = useCallback((currency: Currency) => {
        if (currencies) {
            const filteredArray = {
                ...currencies,
                data: currencies.data.filter(r => r.id !== currency.id),
                count: currencies.count - 1
            };
            setIsDataTableLoading(true);
            API.deleteCurrency(currency.id).then(() => {
                if (currency) {
                    setCurrencies(filteredArray);
                    setIsDataTableLoading(false);
                }
            }).catch(e => ErrorHandler(e, `delete${APILink.GET_CURRENCY.replace('$currency', currency.id.toString())}`, toast, handleError));
        }
    }, [currencies, setIsDataTableLoading, setCurrencies, toast, handleError]);

    const onCryptoChanged = useCallback((s: string | number) => {
        const n = typeof s === "string" ? parseInt(s) : s;
        let request: CurrencyDataTableRequest = {
            ...dataTableRequest,
            page: 1
        };
        if (n > 0) {
            request = {...request, crypto: n as 0 | 1 | 2};
        } else if (typeof request.crypto !== "undefined") {
            delete request.crypto;
        }
        persistDataTableRequest(request);
        sendRequest(request);
    }, [sendRequest, dataTableRequest, persistDataTableRequest]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);


    useEffect(() => {
        setTitle(t('admin.currencies.title'));
        dispatch(setSidebarTitle(t('admin.currencies.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        const request = {...dataTableRequest, search: newID[1]};
                        persistDataTableRequest(request);
                        sendRequest(request);
                    }
                } else {
                    sendRequest(dataTableRequest);
                }
            }).catch();
        }
    }, [t, user]);

    const filterChipsItems = useMemo<FilterChipDefinition[]>(() => {
        if (!crypto) {
            return [];
        }
        const label = crypto === 1 ? t('admin.currencies.table.crypto') : t('admin.currencies.table.fiat');
        return [{
            key: `crypto-${crypto}`,
            label: `${t('admin.currencies.table.crypto')}: ${label}`,
            onRemove: () => onCryptoChanged('0')
        }];
    }, [crypto, t, onCryptoChanged]);

    const filters = useMemo(() => {
        return <DropdownInput
            withAllButton={true}
            name={t('admin.currencies.table.crypto')}
            selected={crypto}
            onChange={onCryptoChanged}
            onSelectAll={() => onCryptoChanged('')}
            list={[{id: 1, name: t('admin.currencies.table.crypto')}, {
                id: 2,
                name: t('admin.currencies.table.fiat')
            }]}
            menuClassName="w-full -mt-5"
        />
    }, [crypto, onCryptoChanged, t]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
                {user?.user.permissions.includes('create currency') &&
                    <Button onClick={() => navigate('/app/admin/currencies/create')}
                            className="bg-black text-white">{t('admin.currencies.new_currency.button')}</Button>}
            </div>
        )
    }, [user, refresh]);

    const dataTable = useMemo(() => {
        if (!currencies)
            return <></>;

        return <DataTable {...dataTableRequest}
                          total_pages={currencies.pages.total}
                          count={currencies.count}
                          nodata={currencies.data.length === 0}
                          inProgress={isDataTableLoading}
                          filters={filters}
                          fullWidthFilters={<FilterChips className="px-4 -mt-6 mb-6" items={filterChipsItems}/>}
                          rightPanel={actionsPanel}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t("admin.currencies.table.code"), sortBy: 'code'},
            {name: t("admin.currencies.table.name"), sortBy: 'name'},
            {name: t("admin.currencies.table.crypto"), sortBy: 'isCrypto'},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {currencies.data.map((country, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD>{country.code.toUpperCase()}</TD>
                        <TD>{country.name}</TD>
                        <TD>{country.isCrypto ?
                            <Checkbox color={'teal'} crossOrigin={''} checked={true} disabled={true}/> : ''}</TD>
                        <TD className="text-right">
                            <>
                                {user?.user.permissions.includes('update currency') &&
                                    <Button onClick={() => navigate(`/app/admin/currencies/${country.id}`)}
                                            className="px-5 py-2 text-white bg-black lg:mr-2"><FontAwesomeIcon
                                        icon={solid("pen-to-square")}/></Button>}
                                {user?.user.permissions.includes('delete currency') &&
                                    <Button onClick={() => deleteCurrency(country)}
                                            className="px-5 py-2 text-red-300 bg-black"><FontAwesomeIcon
                                        icon={solid("trash")}/></Button>}
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, filters, filterChipsItems, isDataTableLoading, currencies, deleteCurrency, navigate, onDataTableChanged, user?.user.permissions, t]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {dataTable}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Currencies);
